import React from "react";
import ReactDOM from "react-dom/client";

// ① Third-party CSS
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

// ② App styles
import "./index.css";

// ③ Design-system theme
import "./ds/theme/theme.css";

import App from "./App";
import reportWebVitals from "./reportWebVitals";

/* =====================================================================
   GLOBAL ERROR TRAP  (runs before React mounts)
   =====================================================================
   The blank page is intermittent and hard to reproduce, so instead of
   trying to catch it live we:

     1. Record every uncaught error / promise rejection into localStorage
        (a ring buffer of the last 15), with message, file, line, stack,
        and timestamp. Survives the blank page — read it back afterwards.

     2. If the app root ends up EMPTY (the white screen), replace it with a
        visible card showing the actual error and a "Reset & reload" button,
        so users self-recover and you can SEE what threw.

   This sits BELOW React's error boundaries and catches things they can't:
   errors during module load, store rehydration, or inside async thunks.

   HOW TO READ THE LOG (in DevTools console, on any page):
     JSON.parse(localStorage.getItem("__erp_errors__"))
   or copy it to paste elsewhere:
     copy(localStorage.getItem("__erp_errors__"))
   ===================================================================== */
(function installErrorTrap() {
  const KEY = "__erp_errors__";
  const MAX = 15;

  function record(kind, info) {
    try {
      const arr = JSON.parse(localStorage.getItem(KEY) || "[]");
      arr.push({ t: new Date().toISOString(), kind, url: window.location.href, ...info });
      while (arr.length > MAX) arr.shift();
      localStorage.setItem(KEY, JSON.stringify(arr));
    } catch (e) { /* storage full/disabled — ignore */ }
    // eslint-disable-next-line no-console
    console.error("[erp-trap]", kind, info.message, info.stack || "");
  }

  function lastError() {
    try {
      const arr = JSON.parse(localStorage.getItem(KEY) || "[]");
      return arr[arr.length - 1] || null;
    } catch (e) { return null; }
  }

  function esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }

  // If #root is empty a moment after an error, paint the error over the blank.
  function paintIfBlank() {
    setTimeout(() => {
      const root = document.getElementById("root");
      if (!root || root.childElementCount > 0) return;   // app rendered fine
      const e = lastError() || {};
      root.innerHTML =
        '<div style="min-height:100vh;display:flex;align-items:center;justify-content:center;' +
        'font-family:system-ui,sans-serif;background:#f8fafc;padding:24px">' +
          '<div style="max-width:640px;width:100%;background:#fff;border:1px solid #e2e8f0;' +
          'border-radius:12px;box-shadow:0 4px 12px rgba(0,0,0,.08);padding:28px">' +
            '<div style="font-size:1.6rem">⚠️</div>' +
            '<h2 style="margin:.5rem 0;color:#0f172a;font-size:1.15rem">The app hit an error while loading</h2>' +
            '<p style="color:#64748b;font-size:.9rem;margin:.25rem 0 1rem">' +
              'Your saved data on the server is safe. Reset clears local settings only.</p>' +
            '<pre style="background:#0f172a;color:#e2e8f0;font-size:.75rem;line-height:1.4;' +
            'padding:12px;border-radius:8px;max-height:220px;overflow:auto;white-space:pre-wrap">' +
              esc(e.message || "Unknown error") +
              (e.url2 ? "" : "") +
              (e.source ? "\n\nat " + esc(e.source) + ":" + esc(e.line) : "") +
              (e.stack ? "\n\n" + esc(e.stack) : "") +
            '</pre>' +
            '<div style="margin-top:16px;display:flex;gap:8px">' +
              '<button id="__erp_reset" style="border:none;border-radius:8px;padding:.6rem 1.3rem;' +
              'font-weight:600;color:#fff;background:#1e40af;cursor:pointer">Reset &amp; reload</button>' +
              '<button id="__erp_reload" style="border:1px solid #e2e8f0;border-radius:8px;' +
              'padding:.6rem 1.3rem;font-weight:600;background:#fff;cursor:pointer">Just reload</button>' +
            '</div>' +
          '</div>' +
        '</div>';

      const reset = document.getElementById("__erp_reset");
      const reload = document.getElementById("__erp_reload");
      if (reset) reset.onclick = function () {
        try {
          Object.keys(localStorage)
            .filter((k) => k.startsWith("persist:") || k === KEY)
            .forEach((k) => localStorage.removeItem(k));
        } catch (e) {}
        window.location.replace(window.location.pathname + "?_r=" + Date.now());
      };
      if (reload) reload.onclick = function () { window.location.reload(); };
    }, 1500);
  }

  window.addEventListener("error", (e) => {
    record("error", {
      message: e.message,
      source: e.filename,
      line: e.lineno,
      col: e.colno,
      stack: e.error && e.error.stack ? String(e.error.stack).slice(0, 3000) : null,
    });
    paintIfBlank();
  });

  window.addEventListener("unhandledrejection", (e) => {
    const r = e.reason || {};
    record("promise", {
      message: r.message ? r.message : String(r),
      stack: r.stack ? String(r.stack).slice(0, 3000) : null,
    });
    paintIfBlank();
  });
})();

/* Belt-and-braces: remove any legacy service worker (this app uses none). */
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations()
    .then((regs) => regs.forEach((r) => r.unregister()))
    .catch(() => {});
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

reportWebVitals();
